import { useCallback, useEffect, useState } from 'react'

import { getModifiers } from '@/api/clarityLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { formatMetric } from '@/hooks/clarity/util'
import {
  ModifiersItemResponseModel,
  ModifiersItemsModel,
  ModifiersItemsResponseModel,
} from '@/types/clarityLens'
import { getMappedRegionBranches } from '@/utils'

const BASE_MODIFIERS_ITEMS_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'Modifier',
    accessorKey: 'itemName',
    type: 'text',
  },
  {
    header: '# of Usage',
    accessorKey: 'count',
    type: 'number',
  },
  {
    header: '% of Usage',
    accessorKey: 'usagePercentage',
    type: 'percentage',
  },
  {
    header: 'Incremental Revenue',
    accessorKey: 'revenue',
    type: 'money',
  },
]

function getBranchRegion(
  branchName: string,
  regionMap: Map<string, string[]>
): string {
  for (const [region, branches] of regionMap.entries()) {
    if (branches.includes(branchName)) {
      return region
    }
  }
  return 'Undefined Region'
}

function aggregateBranchDataByRegion(
  branchData: Record<string, number>,
  regionBranchMap: Map<string, string[]>
): Record<string, number> {
  const regionData: Record<string, number> = {}
  const regionCounts: Record<string, number> = {}

  // Group branches by region and sum values
  Object.entries(branchData).forEach(([branchName, value]) => {
    const regionName = getBranchRegion(branchName, regionBranchMap)

    if (!regionData[regionName]) {
      regionData[regionName] = 0
      regionCounts[regionName] = 0
    }

    regionData[regionName] += value
    regionCounts[regionName] += 1
  })

  // Calculate averages
  Object.keys(regionData).forEach((regionName) => {
    regionData[regionName] = regionData[regionName] / regionCounts[regionName]
  })

  return regionData
}

function generateModifiersItemsColumns(
  responseData: ModifiersItemsResponseModel
): ScalrTableColumn[] {
  const baseColumns = [...BASE_MODIFIERS_ITEMS_COLUMNS]
  const { regionBranchMap } = getMappedRegionBranches()

  // Get branch-specific keys from the first item in response
  const firstItem = Object.values(responseData)[0]
  if (firstItem) {
    const { count, revenue, usage_pct, ...branchData } = firstItem

    // Get unique regions from branch data
    const regionData = aggregateBranchDataByRegion(branchData, regionBranchMap)

    Object.keys(regionData).forEach((regionName) => {
      baseColumns.push({
        header: `${regionName} %`,
        accessorKey: regionName,
        type: 'percentage',
      })
    })
  }

  return baseColumns
}

export default function useModifiersItems(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [modifiersItemsChartData, setModifiersItemsChartData] =
    useState<ModifiersItemsModel[]>()
  const [modifiersItemsColumns, setModifiersItemsColumns] = useState<
    ScalrTableColumn[]
  >(BASE_MODIFIERS_ITEMS_COLUMNS)

  const fetchAllData = useCallback(async () => {
    if (!itemGroupName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, columns } = await fetchModifiersItemsData(
        branches,
        startDate,
        endDate,
        itemGroupName,
        sizeFilter,
        temperatureFilter
      )

      setModifiersItemsChartData(data)
      setModifiersItemsColumns(columns)
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [
    branches,
    startDate,
    endDate,
    itemGroupName,
    sizeFilter,
    temperatureFilter,
  ])

  const refetch = useCallback(() => {
    console.log('Refetching modifiers items...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    modifiersItemsChartData,
    modifiersItemsColumns,
    loading,
    error,
    refetch,
  }
}

async function fetchModifiersItemsData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
): Promise<{
  data: ModifiersItemsModel[]
  columns: ScalrTableColumn[]
}> {
  try {
    const response = await getModifiers(
      branches,
      startDate,
      endDate,
      itemGroupName,
      sizeFilter,
      temperatureFilter
    )

    // Generate dynamic columns based on response data
    const columns = generateModifiersItemsColumns(response)

    const data: ModifiersItemsModel[] = Object.entries(response).map(
      ([keyName, value]) => {
        return convertData(keyName, value)
      }
    )

    return {
      data,
      columns,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  keyName: string,
  value: ModifiersItemResponseModel
): ModifiersItemsModel {
  const { count, revenue, usage_pct, ...branchData } = value
  const { regionBranchMap } = getMappedRegionBranches()

  const baseModel: ModifiersItemsModel = {
    itemName: keyName,
    count: formatMetric(count, 0),
    revenue: formatMetric(revenue, 2),
    usagePercentage: formatMetric(usage_pct, 2),
  }

  // Aggregate branch data by region and add to model
  const regionData = aggregateBranchDataByRegion(branchData, regionBranchMap)

  Object.entries(regionData).forEach(([regionName, regionValue]) => {
    baseModel[regionName] = formatMetric(regionValue, 1)
  })

  return baseModel
}
